import { DesignEntity } from '@/lib/enums';
import defaultEntityMetrics from './defaultEntityMetrics';
import includeActivityByFilterState from './includeActivityByFilterState';

/**
 * Default properties for stepping through the hierarchy tree.
 * @return {Object}
 */
function defaultTreePathStep() {
  return {
    uuid: null,
    type: null,
    order: Infinity,
  };
}

/**
 * Default properties for entity maps.
 * @return {Object}
 */
function defaultEntityMap() {
  return {
    [DesignEntity.GROUP]: new Set(),
    [DesignEntity.MANAGER]: new Set(),
    [DesignEntity.ROLE]: new Set(),
    [DesignEntity.ACTIVITY]: new Set(),
  };
}

/**
 * Extends an entity with additional hierarchy properties.
 * @param {Object} entity
 * @return {Object}
 */
function extendEntityProperties(entity) {
  /** The activities that belong to this entity */
  entity.__activities = new Set();
  /** The direct children of this node in the tree hierarchy */
  entity.__children = new Map();
  /** The closest manager above this node in the tree hierarchy */
  entity.__manager = null;
  /** The direct parent of this node in the tree hierarchy */
  entity.__parent = null;
  /** The path of tree steps to this node from its root */
  entity.__path = new Map();
  /** Splits out the path by types */
  entity.__above = defaultEntityMap();
  /** Groups: All entities within this group, inclusive of nested */
  /** Roles: All entities that branch from this role, inclusive of sub-managers */
  entity.__managed = defaultEntityMap();
  /** Groups: Splits out children by type */
  /** Roles: Splits out children by type, including nested groups lead and roles parented */
  entity.__direct = defaultEntityMap();
  // entity.__layers = null;
  /** Groups: All entities within this group, exclusive of nested */
  /** Roles: All entities in the branches between this and inclusive of the next manager */
  entity.__span = defaultEntityMap();
  /** The managed, self and span entity metrics */
  entity.__metrics = defaultEntityMetrics();
}

/**
 * Extends an activity with additional hierarchy properties.
 * @param {Object} activity
 * @return {Object}
 */
function extendActivityProperties(activity) {
  /** The group of the owner of this activity */
  activity.__group = null;
  /** The manager of the owner of this activity */
  activity.__manager = null;
  /** The path of tree steps to this node from its root */
  activity.__path = new Map();
}

/**
 * Iterate through the hierarchy to set manager and path
 * @param {Object} snapshot
 * @param {String} id
 * @param {DesignEntity} type
 * @param {String} [managerId]
 * @param {Map} [path]
 * @returns {void}
 */
function iterateHierarchy(
  snapshot,
  id,
  type,
  managerId = null,
  path = new Map()
) {
  const plural = DesignEntity.toPlural(type);
  const entity = snapshot.entities[plural][id];

  // Set the entity type.
  entity.__type = type;

  // set entity hierarchy
  entity.__manager = managerId;
  entity.__path = new Map(path);

  // prepare next
  const nextManagerId = entity.is_manager ? id : managerId;
  const nextPath = new Map(path);
  const nextStep = defaultTreePathStep();
  nextStep.uuid = id;
  nextStep.type = type;
  nextStep.order = nextPath.size;

  nextPath.set(id, nextStep);

  // iterate children
  const entityChildren = Array.from(entity.__children.entries());
  entityChildren.forEach(([id, type]) => {
    iterateHierarchy(snapshot, id, type, nextManagerId, nextPath);
  });
}

/**
 * Map hierarchy to a snapshot, optionally flattening it
 * @param {Object} snapshot
 * @returns {?Object}
 */
export default function deriveHierarchy({ activityState, snapshot }) {
  if (!snapshot || typeof snapshot !== 'object') {
    return;
  }

  // setup hierarchy objects
  snapshot.__roots = new Map();

  // Set the default entity hierarchy properties on the group.
  for (const id in snapshot.entities.groups) {
    extendEntityProperties(snapshot.entities.groups[id]);
  }

  // Set the default entity hierarchy properties on the role.
  for (const id in snapshot.entities.roles) {
    extendEntityProperties(snapshot.entities.roles[id]);
  }

  // Set the default hierarchy properties on the activity.
  for (const id in snapshot.entities.activities) {
    extendActivityProperties(snapshot.entities.activities[id]);
  }

  // loop through groups for parents and children
  for (const id in snapshot.entities.groups) {
    const group = snapshot.entities.groups[id];
    group.__hours = 0;

    const outer = snapshot.entities.groups[group.group_uuid];
    const lead = snapshot.entities.roles[group.lead_uuid];

    const leadId = group.lead_uuid ?? null;
    const leadGroupId = lead?.group_uuid ?? null;
    const outerId = group.group_uuid ?? null;

    const hasLead = Boolean(lead);
    const hasOuter = Boolean(outer);
    const isLeadInGroup = hasLead && leadGroupId === outerId;

    // set hierarchy
    switch (true) {
      case isLeadInGroup:
        group.__parent = { uuid: leadId, type: DesignEntity.ROLE };
        lead.__children.set(id, DesignEntity.GROUP);
        continue;

      case hasOuter:
        group.__parent = { uuid: outerId, type: DesignEntity.GROUP };
        outer.__children.set(id, DesignEntity.GROUP);
        continue;

      default:
        snapshot.__roots.set(id, DesignEntity.GROUP);
        continue;
    }
  }

  // loop through roles for parents and children
  for (const id in snapshot.entities.roles) {
    const role = snapshot.entities.roles[id];
    role.__layers = null;
    role.__hours = 0;

    const group = snapshot.entities.groups[role.group_uuid];
    const parent = snapshot.entities.roles[role.parent_uuid];

    const groupId = role.group_uuid ?? null;
    const parentId = role.parent_uuid ?? null;
    const parentGroupId = parent?.group_uuid ?? null;

    const hasGroup = Boolean(group);
    const hasParent = Boolean(parent);
    const isParentInGroup = hasParent && parentGroupId === groupId;

    // set hierarchy
    switch (true) {
      case isParentInGroup:
        role.__parent = { uuid: parentId, type: DesignEntity.ROLE };
        parent.__children.set(id, DesignEntity.ROLE);
        continue;

      case hasGroup:
        role.__parent = { uuid: groupId, type: DesignEntity.GROUP };
        group.__children.set(id, DesignEntity.ROLE);
        continue;

      default:
        snapshot.__roots.set(id, DesignEntity.ROLE);
        continue;
    }
  }

  // iterate through hierarchy for path and manager
  try {
    const roots = Array.from(snapshot.__roots.entries());
    roots.forEach(([id, type]) => iterateHierarchy(snapshot, id, type));
  } catch (e) {
    console.debug('Error iterating __roots map in getHierarchy ', snapshot);
    console.error(e);
  }

  // loop through groups for above, managed, direct, and spans
  for (const id in snapshot.entities.groups) {
    const group = snapshot.entities.groups[id];
    const lead = snapshot.entities.roles[group.lead_uuid] ?? null;
    const outer = snapshot.entities.groups[group.group_uuid] ?? null;
    const manager = snapshot.entities.roles[group.__manager] ?? null;

    const managerId = group.__manager ?? null;
    const isManagerInGroup = manager?.group_uuid === group.group_uuid;

    lead?.__direct[DesignEntity.GROUP].add(group.uuid);

    if (outer) {
      outer.__span[DesignEntity.GROUP].add(group.uuid);
      outer.__metrics.span.total.groups += 1;
    }

    if (manager) {
      manager.__span[DesignEntity.GROUP].add(group.uuid);
      manager.__metrics.span.total.groups += 1;
    }

    group.__children.forEach((childType, childId) => {
      group.__direct[childType].add(childId);
    });

    group.__path.forEach(({ type, uuid }) => {
      const plural = DesignEntity.toPlural(type);
      const entity = snapshot.entities[plural][uuid];
      group.__above[type].add(uuid);

      if (entity.__managed[DesignEntity.GROUP].has(group.uuid)) {
        return;
      }

      entity.__managed[DesignEntity.GROUP].add(group.uuid);
      entity.__metrics.managed.total.groups += 1;
    });
  }

  // loop through roles for above, managed, direct, and spans
  for (const id in snapshot.entities.roles) {
    const role = snapshot.entities.roles[id];

    const budget = +role.budget;
    const fte = +role.fte;
    const parent = snapshot.entities.roles[role.parent_uuid] ?? null;
    const group = snapshot.entities.groups[role.group_uuid] ?? null;
    const manager = snapshot.entities.roles[role.__manager] ?? null;

    const groupId = role.group_uuid ?? null;
    const managerId = role.__manager ?? null;
    const managerGroupId = manager?.group_uuid ?? null;
    const hasManager = Boolean(manager);
    const isManagerInGroup = hasManager && managerGroupId === groupId;

    parent?.__direct[DesignEntity.ROLE].add(role.uuid);
    if (group) {
      group.__span[DesignEntity.ROLE].add(role.uuid);
      group.__metrics.span.total.roles += 1;
      group.__metrics.span.total.fte += fte;
      group.__metrics.span.total.budget += budget;

      if (role.is_manager) {
        group.__span[DesignEntity.MANAGER].add(role.uuid);
        group.__metrics.span.total.managers += 1;
      }
    }

    if (manager) {
      manager.__span[DesignEntity.ROLE].add(role.uuid);
      manager.__metrics.span.total.roles += 1;
      manager.__metrics.span.total.fte += fte;
      manager.__metrics.span.total.budget += budget;

      if (role.is_manager) {
        manager.__span[DesignEntity.MANAGER].add(role.uuid);
        manager.__metrics.span.total.managers += 1;
      }
    }

    role.__children.forEach((childType, childId) => {
      role.__direct[childType].add(childId);

      const plural = DesignEntity.toPlural(childType);
      const child = snapshot.entities[plural][childId];
      if (child.is_manager) {
        role.__direct[DesignEntity.MANAGER].add(childId);
      }
    });

    role.__path.forEach(({ type, uuid }) => {
      const plural = DesignEntity.toPlural(type);
      const above = snapshot.entities[plural][uuid];

      if (
        type === DesignEntity.ROLE &&
        above.is_manager &&
        !above.__managed[DesignEntity.ROLE].has(role.uuid)
      ) {
        above.__managed[DesignEntity.ROLE].add(role.uuid);
        above.__metrics.managed.total.roles += 1;
        above.__metrics.managed.total.budget += budget;
        above.__metrics.managed.total.fte += fte;

        const layers = role.__layer - above.__layer;
        above.__layers = Math.max(above.__layers || 0, layers);
      }

      if (above.is_manager) {
        role.__above[DesignEntity.MANAGER].add(uuid);
      }

      if (
        type === DesignEntity.GROUP &&
        !above.__managed[DesignEntity.ROLE].has(role.uuid)
      ) {
        above.__metrics.managed.total.roles += 1;
        above.__metrics.managed.total.budget += budget;
        above.__metrics.managed.total.fte += fte;
      }

      role.__above[type].add(uuid);
      if (
        role.is_manager &&
        above.is_manager &&
        !above.__managed[DesignEntity.MANAGER].has(role.uuid)
      ) {
        above.__managed[DesignEntity.MANAGER].add(role.uuid);
        above.__metrics.managed.total.managers += 1;
      }
    });
  }

  // loop through activities to set paths
  const activityPaths = new Map();
  for (const id in snapshot.entities.activities) {
    const activity = snapshot.entities.activities[id];
    const includeActivity =
      activity.owner_type === DesignEntity.ROLE &&
      includeActivityByFilterState({ activity, activityState });

    const ownerPlural = DesignEntity.toPlural(activity.owner_type);
    const owner = snapshot.entities[ownerPlural][activity.owner_uuid];

    if (owner) {
      owner.__activities.add(id);

      const activityHours = includeActivity ? activity.hours : 0;

      owner.__metrics.self.total.hours += activityHours;
      owner.__hours = owner.__hours
        ? (owner.__hours += activityHours)
        : activityHours;
    }

    const manager = snapshot.entities.roles[owner?.__manager] ?? null;

    if (manager) {
      manager.__span[DesignEntity.ACTIVITY].add(id);
      if (includeActivity) {
        manager.__metrics.span.total.activities += 1;
        manager.__metrics.span.total.hours += activity.hours;
      }
    }

    const parent = snapshot.entities.roles[owner?.__parent?.uuid] ?? null;
    parent?.__direct[DesignEntity.ACTIVITY].add(id);

    const group = owner?.group_uuid
      ? snapshot.entities.groups[owner.group_uuid]
      : null;

    if (group && includeActivity) {
      group.__hours = group.__hours
        ? (group.__hours += activity.hours)
        : activity.hours;
      group.__metrics.self.total.hours += activity.hours;
      group.__metrics.span.total.activities += 1;
      group.__metrics.span.total.hours += activity.hours;
    }

    activity.__group = owner?.group_uuid ?? null;
    activity.__manager = owner?.__manager ?? null;

    // set path
    switch (true) {
      case !owner:
        activity.__path = new Map();
        break;

      case activityPaths.has(owner.uuid):
        activity.__path = new Map(activityPaths.get(owner.uuid));
        break;

      default:
        const ownerStep = defaultTreePathStep();
        ownerStep.uuid = activity.owner_uuid;
        ownerStep.type = activity.owner_type;
        ownerStep.order = owner.__path.size;

        activity.__path = new Map(owner.__path);
        activity.__path.set(owner.uuid, ownerStep);
        activityPaths.set(owner.uuid, new Map(activity.__path));
    }

    // iterate path
    activity.__path.forEach(({ type, uuid }) => {
      const plural = DesignEntity.toPlural(type);
      const entity = snapshot.entities[plural][uuid];
      if (entity.__managed[DesignEntity.ACTIVITY].has(id)) {
        return;
      }

      entity.__managed[DesignEntity.ACTIVITY].add(id);
      if (includeActivity) {
        entity.__metrics.managed.total.activities += 1;
        entity.__metrics.managed.total.hours += activity.hours;
      }
    });
  }
}
